import { TriangleAlertIcon } from 'lucide-react'
import * as React from 'react'

import { Button } from '@ui/components/ui/button'
import { cn } from '@ui/lib/utils'

export interface ErrorStateProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Was nicht funktioniert hat — sachlich, ohne Entschuldigung. */
  title?: string
  /** Was der Nutzer jetzt tun kann. */
  description?: string
  /** Technische Details für den Support. Stehen eingeklappt darunter. */
  details?: string
  /** Wird als "Erneut versuchen" angeboten, wenn gesetzt. */
  onRetry?: () => void
}

/**
 * Fehlerzustand innerhalb einer Seite (nicht der Absturz eines ganzen Moduls —
 * dafür ist die Error Boundary der Shell zuständig).
 *
 * @example
 * ```tsx
 * {query.isError && (
 *   <ErrorState
 *     description="Die Vorgänge konnten nicht geladen werden."
 *     details={query.error.message}
 *     onRetry={() => query.refetch()}
 *   />
 * )}
 * ```
 */
export function ErrorState({
  title = 'Das hat nicht geklappt',
  description = 'Die Daten konnten nicht geladen werden. Versuchen Sie es erneut.',
  details,
  onRetry,
  className,
  ...props
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-start gap-3 rounded-lg border border-destructive/30 bg-destructive-surface px-4 py-4',
        className,
      )}
      {...props}
    >
      <div className="flex items-start gap-3">
        <TriangleAlertIcon
          aria-hidden="true"
          className="mt-0.5 size-5 shrink-0 text-destructive-strong"
        />
        <div className="space-y-1">
          <p className="font-medium text-destructive-strong">{title}</p>
          <p className="text-sm text-foreground">{description}</p>
        </div>
      </div>
      {details && (
        <details className="w-full text-sm text-muted-foreground">
          <summary className="cursor-pointer select-none">Technische Details</summary>
          <pre className="mt-2 overflow-x-auto rounded-md bg-card p-3 text-xs">
            {details}
          </pre>
        </details>
      )}
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          Erneut versuchen
        </Button>
      )}
    </div>
  )
}
